<template>
  <b-modal
    :visible="show"
    :title="modalTitle"
    size="md"
    hide-footer
    @hidden="$emit('close')"
  >
    <div v-if="isCreateMode || task">
      <div v-if="!isCreateMode" class="font-weight-bold text-dark mb-4">
        {{ task.name }}
      </div>
      <div v-else class="mb-4">
        <label class="font-weight-bold">{{ $t("SERVICE_VISITS.TASK_NAME") }}</label>
        <b-form-input
          v-model="form.description"
          :disabled="saving"
          autofocus
        />
      </div>

      <div class="mb-4">
        <label class="font-weight-bold">{{ $t("FORM_LABELS.STATUS") }}</label>
        <b-form-select
          v-if="canEdit"
          v-model="form.statusId"
          :options="statusOptions"
          :disabled="saving"
        />
        <span v-else class="badge badge-light font-weight-bold">
          <span
            class="service-visit-task-status-dot d-inline-block rounded-circle mr-2"
            :style="statusDotStyle(task.status)"
          ></span>
          {{ statusName(task.status) }}
        </span>
      </div>

      <div class="mb-4">
        <label class="font-weight-bold">{{ $t("FORM_LABELS.NOTE") }}</label>
        <b-form-textarea
          v-if="canEditNote"
          v-model="form.note"
          rows="5"
          max-rows="8"
          :disabled="saving"
        />
        <div v-else class="border rounded p-3 text-dark white-space-pre-wrap">
          {{ task.note || "-" }}
        </div>
      </div>

      <div class="d-flex justify-content-end">
        <button
          type="button"
          class="btn btn-light mr-2"
          :disabled="saving"
          @click="$emit('close')"
        >
          {{ $t("BUTTON.CANCEL") }}
        </button>
        <button
          v-if="canEdit"
          type="button"
          class="btn btn-primary"
          :disabled="saving || !canSave"
          @click="save"
        >
          {{ $t("BUTTON.SAVE") }}
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: "ServiceVisitBoardTaskModal",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    task: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: "edit"
    },
    itemStatuses: {
      type: Array,
      default: () => []
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    canEditNote: {
      type: Boolean,
      default: false
    },
    saving: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      form: {
        description: "",
        statusId: null,
        note: ""
      }
    };
  },
  computed: {
    isCreateMode() {
      return this.mode === "create";
    },
    modalTitle() {
      return this.isCreateMode
        ? this.$t("SERVICE_VISITS.ADD_TASK")
        : this.$t("SERVICE_VISITS.TASK_DETAILS");
    },
    canSave() {
      if (!this.form.statusId) {
        return false;
      }

      return !this.isCreateMode || this.form.description.trim().length > 0;
    },
    defaultStatusId() {
      const newStatus = this.itemStatuses.find(status => status.slug === "new");

      return newStatus ? newStatus.id : (this.itemStatuses[0] || {}).id || null;
    },
    statusOptions() {
      return this.itemStatuses.map(status => ({
        value: status.id,
        text: status.name
      }));
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(show) {
        if (show) {
          this.resetForm();
        }
      }
    },
    task: {
      immediate: true,
      handler() {
        if (this.show) {
          this.resetForm();
        }
      }
    },
    mode() {
      if (this.show) {
        this.resetForm();
      }
    },
    itemStatuses() {
      if (this.show && this.isCreateMode && !this.form.statusId) {
        this.form.statusId = this.defaultStatusId;
      }
    }
  },
  methods: {
    statusColor(status) {
      return status && status.color ? status.color : "#3699ff";
    },
    statusName(status) {
      return status && status.name ? status.name : "-";
    },
    statusDotStyle(status) {
      return {
        backgroundColor: this.statusColor(status)
      };
    },
    resetForm() {
      if (this.isCreateMode) {
        this.form.description = "";
        this.form.statusId = this.defaultStatusId;
        this.form.note = "";
        return;
      }

      this.form.description = "";
      this.form.statusId = this.task && this.task.status ? this.task.status.id : null;
      this.form.note = this.task && this.task.note ? this.task.note : "";
    },
    save() {
      const payload = { statusId: this.form.statusId };

      if (this.canEditNote) {
        payload.note = this.form.note;
      }

      if (this.isCreateMode) {
        payload.description = this.form.description;
      }

      this.$emit("save", payload);
    }
  }
};
</script>

<style scoped>
.service-visit-task-status-dot {
  height: 8px;
  width: 8px;
}

.white-space-pre-wrap {
  white-space: pre-wrap;
}
</style>
